import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import AdminHeader from "@/components/admin/AdminHeader";
import { useAuth } from "@/contexts/AuthContext";
import { getManualNotificationRecipients, sendManualNotification } from "@/api/adminNotifications";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import ComboboxBasic from "@/components/ComboboxBasic";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

const defaultForm = {
  title: "",
  body: "",
  deeplink: "",
  sendInApp: true,
  sendPush: true,
  targetUserId: "",
};

export default function AdminNotificationsPage() {
  const { user } = useAuth();
  const [form, setForm] = useState(defaultForm);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const { data: recipientsResponse, isLoading: recipientsLoading } = useQuery({
    queryKey: ["admin", "manualNotificationRecipients"],
    queryFn: getManualNotificationRecipients,
    enabled: user?.role === "super_admin",
    staleTime: 5 * 60 * 1000,
  });

  if (user?.role !== "super_admin") {
    return <Navigate to="/admin" replace />;
  }

  const recipientItems = [
    {
      id: "",
      label: "Alla opt-in-användare",
      sublabel: "Skicka till alla som aktiverat denna notiskategori",
    },
    ...((recipientsResponse?.items || []).map((item) => ({
      id: item.id,
      label: item.label,
      sublabel: item.email || "Vald mottagare",
    }))),
  ];

  const onSubmit = async (event) => {
    event.preventDefault();
    if (sending) return;

    const title = form.title.trim();
    const body = form.body.trim();
    const deeplink = form.deeplink.trim();

    if (!title) {
      toast.error("Titel krävs.");
      return;
    }
    if (!body) {
      toast.error("Meddelande krävs.");
      return;
    }
    if (!form.sendInApp && !form.sendPush) {
      toast.error("Välj minst en kanal.");
      return;
    }
    if (deeplink && !deeplink.startsWith("/")) {
      toast.error("Deeplink måste börja med /");
      return;
    }

    const channel = form.sendInApp && form.sendPush
      ? "both"
      : form.sendPush
        ? "push"
        : "inapp";

    setSending(true);
    try {
      const response = await sendManualNotification({
        title,
        body,
        deeplink: deeplink || null,
        channel,
        targetUserId: form.targetUserId || null,
      });
      setResult(response);
      toast.success("Notisen skickades.");
      setForm((current) => ({
        ...current,
        title: "",
        body: "",
      }));
    } catch (error) {
      toast.error(error?.message || "Kunde inte skicka notisen.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen">
      <AdminHeader />
      <main className="container mx-auto grid gap-6 px-4 py-6">
        <Card>
          <CardHeader>
            <CardTitle>Manuella notiser</CardTitle>
            <CardDescription>
              Skicka nyheter och information från Lapstr till användare som har aktiverat den notiskategorin.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="grid gap-6">
              <div className="grid gap-2">
                <Label htmlFor="manual-notification-recipient">Mottagare</Label>
                <ComboboxBasic
                  items={recipientItems}
                  value={form.targetUserId}
                  onChange={(id) => setForm((current) => ({ ...current, targetUserId: id }))}
                  disabled={recipientsLoading || sending}
                  placeholder={recipientsLoading ? "Laddar mottagare..." : "Välj mottagare"}
                  searchPlaceholder="Sök användare"
                  emptyText="Inga användare hittades"
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground">
                  Välj en enskild testanvändare för riktat utskick, eller lämna på alla opt-in-användare.
                </p>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="manual-notification-title">Titel</Label>
                <Input
                  id="manual-notification-title"
                  value={form.title}
                  maxLength={80}
                  onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
                  placeholder="Till exempel: Ny version av Lapstr"
                />
                <p className="text-xs text-muted-foreground">
                  Håll titeln kort och tydlig. Max 80 tecken.
                </p>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="manual-notification-body">Meddelande</Label>
                <Textarea
                  id="manual-notification-body"
                  value={form.body}
                  maxLength={180}
                  rows={5}
                  onChange={(event) => setForm((current) => ({ ...current, body: event.target.value }))}
                  placeholder="Skriv notisens innehåll"
                />
                <p className="text-xs text-muted-foreground">
                  In-app-notiser ska vara korta och lättlästa i inkorgen. Max 180 tecken.
                </p>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="manual-notification-deeplink">Deeplink (valfri)</Label>
                <Input
                  id="manual-notification-deeplink"
                  value={form.deeplink}
                  maxLength={500}
                  onChange={(event) => setForm((current) => ({ ...current, deeplink: event.target.value }))}
                  placeholder="/account/about#support-card"
                />
                <p className="text-xs text-muted-foreground">
                  Använd intern appväg som börjar med <code>/</code>.
                </p>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <div className="flex items-center justify-between rounded-md border px-3 py-3">
                  <div>
                    <Label htmlFor="manual-notification-inapp" className="text-sm font-medium">Skicka in-app</Label>
                    <p className="text-xs text-muted-foreground">Visas i användarens notisinkorg.</p>
                  </div>
                  <Switch
                    id="manual-notification-inapp"
                    checked={form.sendInApp}
                    onCheckedChange={(checked) => setForm((current) => ({ ...current, sendInApp: checked }))}
                  />
                </div>

                <div className="flex items-center justify-between rounded-md border px-3 py-3">
                  <div>
                    <Label htmlFor="manual-notification-push" className="text-sm font-medium">Skicka push</Label>
                    <p className="text-xs text-muted-foreground">Försöker leverera push till varje opt-in-användare.</p>
                  </div>
                  <Switch
                    id="manual-notification-push"
                    checked={form.sendPush}
                    onCheckedChange={(checked) => setForm((current) => ({ ...current, sendPush: checked }))}
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <Button type="submit" disabled={sending}>
                  {sending ? "Skickar…" : "Skicka notis"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={sending}
                  onClick={() => {
                    setForm(defaultForm);
                    setResult(null);
                  }}
                >
                  Rensa
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Sammanfattning</CardTitle>
            <CardDescription>Senaste körningen för manuellt utskick.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-2 text-sm">
            <div>Målgrupp: <span className="text-foreground">{Number(result?.targetedUsers ?? 0)}</span></div>
            <div>Skapade in-app-notiser: <span className="text-foreground">{Number(result?.inAppCreated ?? 0)}</span></div>
            <div>Push-försök: <span className="text-foreground">{Number(result?.pushAttemptedUsers ?? 0)}</span></div>
            <div>Push uppskjutna p.g.a. tysta timmar: <span className="text-foreground">{Number(result?.pushDeferredUsers ?? 0)}</span></div>
            <div>Push skickade: <span className="text-foreground">{Number(result?.pushSent ?? 0)}</span></div>
            <div>Push borttagna prenumerationer: <span className="text-foreground">{Number(result?.pushRemoved ?? 0)}</span></div>
            <div>Push-fel: <span className="text-foreground">{Number(result?.pushErrors ?? 0)}</span></div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
